import { cn } from "@/lib/cn";
import type { Placement } from "@floating-ui/react";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useClientPoint,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import * as React from "react";
import { useEffect } from "react";

interface TooltipOptions {
  initialOpen?: boolean;
  placement?: Placement;
  offset?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  delay?: number;
  /** When true, the tooltip tracks the mouse cursor instead of anchoring to the trigger. */
  followCursor?: boolean;
}

function useTooltip({
  initialOpen = false,
  placement = "top",
  offset: customOffset = 5,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  delay = 200,
  followCursor = false,
}: TooltipOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    strategy: "fixed",
    transform: true,
    middleware: [
      offset(customOffset),
      flip({
        crossAxis: placement.includes("-"),
        fallbackAxisSideDirection: "start",
        padding: 5,
      }),
      shift({ padding: 5 }),
    ],
  });

  useEffect(() => {
    if (!open) return;
    const refEl = data.refs.reference.current;
    if (!refEl || !(refEl instanceof HTMLElement)) return;

    // Helper to find all scrollable ancestors
    function getScrollableAncestors(el: HTMLElement) {
      const ancestors: (HTMLElement | Window)[] = [];
      let parent = el.parentElement;
      while (parent) {
        const style = getComputedStyle(parent);
        const overflowY = style.overflowY;
        if (
          /(auto|scroll|overlay)/.test(overflowY) &&
          parent.scrollHeight > parent.clientHeight
        ) {
          ancestors.push(parent);
        }
        parent = parent.parentElement;
      }
      // Always include window as a fallback
      ancestors.push(window);
      return ancestors;
    }

    const handleScroll = () => {
      setOpen(false);
    };

    const ancestors = getScrollableAncestors(refEl);
    for (const ancestor of ancestors) {
      ancestor.addEventListener("scroll", handleScroll, true);
    }
    return () => {
      for (const ancestor of ancestors) {
        ancestor.removeEventListener("scroll", handleScroll, true);
      }
    };
  }, [open, setOpen, data.refs.reference]);

  const context = data.context;

  const hover = useHover(context, {
    move: false,
    enabled: controlledOpen == null,
    delay,
  });
  const focus = useFocus(context, {
    enabled: controlledOpen == null,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const clientPoint = useClientPoint(context, { enabled: followCursor });

  const interactions = useInteractions([
    hover,
    focus,
    dismiss,
    role,
    clientPoint,
  ]);
  const { isMounted, styles } = useTransitionStyles(context, {
    duration: 200,
  });

  return React.useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      isMounted,
      styles,
    }),
    [open, setOpen, interactions, data, isMounted, styles],
  );
}

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = React.createContext<ContextType>(null);

const useTooltipContext = () => {
  const context = React.useContext(TooltipContext);

  if (context == null) {
    throw new Error("Tooltip components must be wrapped in <Tooltip />");
  }

  return context;
};

function Tooltip({
  children,
  ...options
}: { children: React.ReactNode } & TooltipOptions) {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const tooltip = useTooltip(options);
  return (
    <TooltipContext.Provider value={tooltip}>
      {children}
    </TooltipContext.Provider>
  );
}

const TooltipTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLElement> & { asChild?: boolean }
>(function TooltipTrigger(
  { children, className, asChild = false, ...props },
  propRef,
) {
  "use no memo";
  const context = useTooltipContext();
  const childrenRef = (children as any).ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...(children.props as any),
        "data-state": context.open ? "open" : "closed",
      }),
    );
  }

  return (
    <button
      ref={ref}
      // The user can style the trigger based on the state
      data-state={context.open ? "open" : "closed"}
      {...context.getReferenceProps(props)}
      className={cn("tw-outline-none focus-visible:tw-outline-none", className)}
    >
      {children}
    </button>
  );
});

const TooltipContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement> & { className?: string }
>(function TooltipContent({ style, className, ...props }, propRef) {
  const context = useTooltipContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  if (!context.isMounted) return null;

  return (
    <FloatingPortal>
      <div
        className={cn(
          "tw-z-[9999] !tw-transition-opacity !tw-duration-200",
          className,
        )}
        ref={ref}
        style={{
          ...context.floatingStyles,
          ...context.styles,
          ...style,
        }}
        {...context.getFloatingProps(props)}
      />
    </FloatingPortal>
  );
});

const TooltipText = React.forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement>
>(function TooltipText({ children, className, ...props }, propRef) {
  return (
    <span
      {...props}
      ref={propRef}
      className={cn(
        "tw-flex tw-w-max tw-items-center tw-rounded-lg tw-bg-blue-base tw-px-3 tw-py-2 tw-font-sans tw-text-[13px]/tight tw-font-normal tw-text-white",
        className,
      )}
    >
      {children}
    </span>
  );
});

Tooltip.Trigger = TooltipTrigger;
Tooltip.Content = TooltipContent;
Tooltip.Text = TooltipText;

export default Tooltip;
